import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { aaiApi } from '../services/aaiApi';
import type { PersonIntakeCard, PersonIntakeState } from '../types/aai';
import '../components/orbit-deck/deck.css';

/*
 * The person-first intake. Public, no account, phone-shaped.
 *
 * A Solution Partner is on stage with a code on the slide. Whoever scans it has
 * no login, is not going to make one there and then, and is answering about
 * themselves: their work, their tools, their job, how far into AI they are.
 *
 * A SESSION, NOT A FORM
 * ---------------------
 * One question at a time, and the server decides which one. It used to be a
 * wizard: the browser held every answer, walked a fixed list, and posted the
 * lot at the end, so closing the tab lost everything and every person was asked
 * the same words in the same order.
 *
 * Now each question is worded for the person answering it, out of what they
 * have already said. A financial controller is asked about month-end close; a
 * warehouse lead is not. The wording comes from the same interviewer that
 * already voices the business walk and participant onboarding, and it falls
 * back to the registry's own question when the model is unavailable, so the
 * worst case here is the intake that shipped before it.
 *
 * WHAT IS ASKED, AND IN WHAT ORDER
 * --------------------------------
 * Who they are, then their own work, and their employer LAST. Every other
 * intake in the product starts from a company and works down to the people in
 * it. This one is the inversion: the company is not the subject, it is a label
 * put on at the end so that several people from the same place can be read
 * together afterwards.
 *
 * Last, but asked. A blank employer used to land as a company called "Not
 * said": one person who could never be read beside anybody, which is the single
 * thing this question exists to make possible.
 */

/* Where a session is remembered, per code. Losing an answer because a phone
   locked is the failure this whole shape exists to prevent, so the id survives
   a reload and the server holds everything else. */
const RESUME_KEY = 'person_intake_session';

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-canvas px-4 py-8 sm:py-14">
    <div className="mx-auto w-full max-w-[560px]">{children}</div>
  </div>
);

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-panel border border-line bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)] sm:p-8">
    {children}
  </div>
);

const inputClass =
  'w-full rounded-act border border-line-strong bg-white px-3 py-2.5 text-[14px] text-ink '
  + 'outline-none transition-colors placeholder:text-ink-soft focus:border-ink';

const optionText = (o: PersonIntakeCard['options'][number]) => {
  if (o.subtitle) return `${o.label} (${o.subtitle})`;
  return o.label;
};

/* The deck's own pills, so this page reads as the same cards the partner and
   the Room use. */
const pillClass = (on: boolean) => {
  if (on) return 'odeck-chip picked';
  return 'odeck-chip';
};

/** The control for one question. The question's own words sit above it. */
export const Answer: React.FC<{
  card: Pick<PersonIntakeCard, 'key' | 'kind' | 'options' | 'placeholder'>;
  value: unknown;
  onChange: (next: unknown) => void;
}> = ({ card, value, onChange }) => {
  const chosen = Array.isArray(value) ? (value as string[]) : [];

  if (card.kind === 'longtext') {
    return (
      <textarea
        id={`q-${card.key}`}
        rows={4}
        autoFocus
        value={(value as string) || ''}
        placeholder={card.placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
      />
    );
  }

  /* Selects are pills rather than a dropdown. On a phone, held one-handed in a
     room, a tap beats opening a picker, and every option stays readable, which
     is what makes a 1 to 10 scale legible at all. */
  if (card.kind === 'select') {
    return (
      <div className="flex flex-wrap gap-2">
        {card.options.map((o) => (
          <button key={o.value} type="button" onClick={() => onChange(o.value)} className={pillClass(value === o.value)}>
            {optionText(o)}
          </button>
        ))}
      </div>
    );
  }

  if (card.kind === 'multiselect') {
    return (
      <div className="flex flex-wrap gap-2">
        {card.options.map((o) => {
          const on = chosen.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange(on ? chosen.filter((v) => v !== o.value) : [...chosen, o.value])}
              className={pillClass(on)}
            >
              {optionText(o)}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <input
      id={`q-${card.key}`}
      type={card.kind === 'number' ? 'number' : 'text'}
      autoFocus
      value={(value as string) || ''}
      placeholder={card.placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={inputClass}
    />
  );
};

export const PersonIntake: React.FC = () => {
  const [params] = useSearchParams();
  const ref = params.get('ref') || '';

  const [partnerName, setPartnerName] = useState('your Solution Partner');
  const [state, setState] = useState<PersonIntakeState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [company, setCompany] = useState('');
  const [draft, setDraft] = useState<unknown>(undefined);

  const forget = useCallback(() => {
    try { localStorage.removeItem(RESUME_KEY); } catch { /* private window */ }
  }, []);

  const remember = useCallback((sessionId: string) => {
    try { localStorage.setItem(RESUME_KEY, `${sessionId}|${ref}`); } catch { /* private window */ }
  }, [ref]);

  /* The partner's name, and whether this code is still good. Then whatever
     session this phone already had, if it is for this same code. */
  const load = useCallback(async () => {
    if (!ref) {
      setFailed('This link is missing its code. Ask for a new one.');
      setLoading(false);
      return;
    }
    try {
      const r = await aaiApi.personIntakeQuestions(ref);
      if (r.data.error) setFailed(r.data.error);
      if (r.data.partner) setPartnerName(r.data.partner.name);
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } } };
      setFailed(e.response?.data?.detail || 'This code is not valid.');
      setLoading(false);
      return;
    }

    /* A link a partner sent to one person carries their session: they carry
       on where they, or their partner for them, left off. A shared code has
       none and is refused here, which is the normal case for a slide. */
    try {
      const invited = await aaiApi.readInvitedPersonIntake(ref);
      setState(invited.data);
      remember(invited.data.session_id);
      setLoading(false);
      return;
    } catch {
      /* Not a personal link. */
    }

    let held = '';
    try { held = localStorage.getItem(RESUME_KEY) || ''; } catch { held = ''; }
    const [sessionId, forCode] = held.split('|');
    if (sessionId && forCode === ref) {
      try {
        const r = await aaiApi.readPersonIntakeSession(ref, sessionId);
        setState(r.data);
      } catch {
        /* Expired, or from a phone that answered weeks ago. Start clean rather
           than explaining a session the person never knew they had. */
        forget();
      }
    }
    setLoading(false);
  }, [ref, forget, remember]);
  useEffect(() => { load(); }, [load]);

  /* A card their partner filled in starts with that answer in it. */
  const heldKey = state?.question?.key || '';
  const heldFill = state?.question?.confirming ? state.question.prefill : undefined;
  useEffect(() => {
    if (heldFill !== undefined) setDraft(heldFill);
  }, [heldKey, heldFill]);

  const begin = async () => {
    setBusy(true);
    setFailed('');
    try {
      const r = await aaiApi.startPersonIntake({ ref, name, email, job_title: jobTitle });
      setState(r.data);
      remember(r.data.session_id);
      setDraft(undefined);
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } } };
      setFailed(e.response?.data?.detail || 'Could not start. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const send = async (skipped: boolean) => {
    if (!state?.question) return;
    setBusy(true);
    setFailed('');
    try {
      const r = await aaiApi.answerPersonIntake({
        ref,
        session_id: state.session_id,
        key: state.question.key,
        value: skipped ? null : draft,
        skipped,
      });
      setState(r.data);
      setDraft(undefined);
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } } };
      setFailed(e.response?.data?.detail || 'Could not save that. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    if (!state) return;
    setBusy(true);
    setFailed('');
    try {
      await aaiApi.finishPersonIntake({ ref, session_id: state.session_id, company_name: company });
      setState({ ...state, stage: 'done' });
      forget();
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } } };
      setFailed(e.response?.data?.detail || 'Could not send that. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <Shell><Card><p className="text-[14px] text-ink-dim">One moment.</p></Card></Shell>;
  }

  if (failed && !state) {
    return <Shell><Card><p className="text-[14px] text-ink">{failed}</p></Card></Shell>;
  }

  if (state?.stage === 'done') {
    return (
      <Shell>
        <Card>
          <div className="mb-3 grid h-10 w-10 place-items-center rounded-full bg-ok-soft">
            <Check size={18} className="text-ok-dim" />
          </div>
          <h1 className="mb-2 text-[20px] font-semibold leading-[1.25] text-ink">Thank you.</h1>
          <p className="text-[14px] leading-[22px] text-ink-muted">
            {state.client_bound && state.joined
              && `${partnerName} has what you said. You are in your team now, and an email is on its way with how to sign in.`}
            {state.client_bound && !state.joined
              && `${partnerName} has what you said, and will add you to your team. You will get an email with how to sign in when that is done.`}
            {!state.client_bound
              && `${partnerName} has what you said. Nothing else is needed from you, and there is no account to set up.`}
          </p>
        </Card>
      </Shell>
    );
  }

  const question = state?.question || null;
  const answered = state?.answered ?? 0;
  const total = state?.total ?? 0;
  /* The employer screen is one more thing to do, so the bar counts it. Showing
     100% while a screen remains is the small lie that makes people close tabs. */
  const pct = total > 0 ? Math.round((answered / (total + 1)) * 100) : 0;
  const given = draft !== undefined && draft !== '' && !(Array.isArray(draft) && draft.length === 0);

  return (
    <Shell>
      <div className="mb-5 flex items-center justify-between gap-3">
        <span className="text-[12.5px] font-semibold text-ink-muted">{partnerName}</span>
        {state && (
          <span className="font-sans text-[12px] tabular-nums text-ink-soft">{answered} of {total}</span>
        )}
      </div>
      {state && (
        <div className="mb-5 h-1 w-full overflow-hidden rounded-full bg-line">
          <div
            className="h-full rounded-full bg-ink transition-[width] duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      {state?.stage === 'asking' && question && (
        <div className="odeck-frame">
          <div className="odeck-frame-head">
            <span className="fk">Your cards<small>{Math.max(total - answered, 0)} to go</small></span>
            <span className="odeck-count">CARD <b>{String(Math.min(answered + 1, Math.max(total, 1))).padStart(2, '0')}</b> · {String(Math.max(total, 1)).padStart(2, '0')}</span>
          </div>
          <div className="rounded-[14px] border border-[var(--aa-hairline)] bg-white px-6 pb-5 pt-5 shadow-[0_14px_36px_rgba(24,25,29,0.08)] sm:px-7">
            <div className="odeck-top">
              <span className="odeck-num">CARD {String(Math.min(answered + 1, Math.max(total, 1))).padStart(2, '0')} · {String(Math.max(total, 1)).padStart(2, '0')}</span>
              <span className={question.confirming ? 'odeck-tag confirm' : 'odeck-tag ask'}>
                {question.confirming ? 'Filled in for you' : question.block_title}
              </span>
            </div>
            <h1 className="m-0 text-[21px] font-bold leading-[1.25] tracking-[-0.01em] text-ink">{question.question}</h1>
            {question.why && <p className="odeck-why">{question.why}</p>}
            {question.confirming && (
              <p className="mb-0 mt-3 rounded-[10px] bg-[var(--aa-card)] px-3 py-2 text-[12.5px] leading-[19px] text-ink">
                {question.filled_by || partnerName} filled this in for you. Keep it if it is right, or change it.
              </p>
            )}
            <div className="mt-6">
              <Answer card={question} value={draft} onChange={setDraft} />
            </div>
            {failed && <p className="odeck-error mt-4">{failed}</p>}
            <div className="mt-7 flex items-center justify-between gap-3 border-t border-line-soft pt-4">
              {/* Passing is recorded as not asked rather than answered blank, so
                  a question that does not apply stops being asked instead of
                  sitting in the answers as an empty one. */}
              <button
                type="button"
                disabled={busy}
                onClick={() => send(true)}
                className="text-[13px] font-semibold text-ink-soft underline-offset-4 transition-colors hover:text-ink hover:underline disabled:opacity-40"
              >
                {question.confirming ? 'Keep this answer' : 'Skip this'}
              </button>
              <button
                type="button"
                disabled={busy || !given}
                onClick={() => send(false)}
                className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-[13.5px] font-semibold text-white transition-opacity disabled:opacity-40 whitespace-nowrap"
              >
                {busy ? 'Saving' : 'Next'} <ArrowRight size={15} />
              </button>
            </div>
          </div>
          <div className="odeck-frame-foot">
            <span>{question.block_title}</span>
            <span>{answered} answered</span>
          </div>
        </div>
      )}

      {!(state?.stage === 'asking' && question) && (
      <Card>
        {!state && (
          <>
            <h1 className="mb-2 text-[20px] font-semibold leading-[1.25] text-ink">First, who are you?</h1>
            <p className="mb-6 text-[13.5px] leading-[21px] text-ink-muted">
              This is about your work, not your company. It takes about three minutes, and it picks
              up where you left off if you have to stop.
            </p>
            <div className="mb-5">
              <label className="mb-1.5 block text-[14px] font-semibold text-ink" htmlFor="person-name">Your name</label>
              <input id="person-name" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
            </div>
            <div className="mb-5">
              <label className="mb-1.5 block text-[14px] font-semibold text-ink" htmlFor="person-email">Email</label>
              <p className="mb-2 text-[12.5px] text-ink-dim">So {partnerName} can send you what comes out of this.</p>
              <input id="person-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
            </div>
            <div className="mb-8">
              <label className="mb-1.5 block text-[14px] font-semibold text-ink" htmlFor="person-job">What do you do?</label>
              <p className="mb-2 text-[12.5px] text-ink-dim">In your own words. The questions after this are shaped around your answer.</p>
              <input
                id="person-job"
                value={jobTitle}
                placeholder="Financial controller, warehouse lead, designer…"
                onChange={(e) => setJobTitle(e.target.value)}
                className={inputClass}
              />
            </div>
            {failed && <p className="mb-5 text-[13px] text-danger">{failed}</p>}
            <button
              type="button"
              disabled={busy || name.trim().length < 2}
              onClick={begin}
              className="inline-flex items-center gap-2 rounded-act bg-ink px-5 py-2.5 text-[13.5px] font-semibold text-white transition-opacity disabled:opacity-40 whitespace-nowrap"
            >
              {busy ? 'Starting' : 'Start'} <ArrowRight size={15} />
            </button>
          </>
        )}

        {state?.stage === 'employer' && (
          <>
            <h1 className="mb-2 text-[20px] font-semibold leading-[1.25] text-ink">Last one: where do you work?</h1>
            <p className="mb-6 text-[13.5px] leading-[21px] text-ink-muted">
              Asked last because it is not the point. It is how your answers can be read alongside
              your colleagues&rsquo; if they answer too, so it is the one thing we do need. Anything
              you call it is fine.
            </p>
            <input
              value={company}
              placeholder="Company name"
              onChange={(e) => setCompany(e.target.value)}
              className={inputClass}
              aria-label="Company name"
              autoFocus
            />
            {failed && <p className="mt-5 text-[13px] text-danger">{failed}</p>}
            <div className="mt-8 flex justify-end">
              <button
                type="button"
                disabled={busy || company.trim().length < 2}
                onClick={finish}
                className="inline-flex items-center gap-2 rounded-act bg-ink px-5 py-2.5 text-[13.5px] font-semibold text-white transition-opacity disabled:opacity-40 whitespace-nowrap"
              >
                {busy ? 'Sending' : 'Done'} <Check size={15} />
              </button>
            </div>
          </>
        )}
      </Card>
      )}

      <p className="mt-4 px-1 text-center text-[11.5px] leading-[18px] text-ink-soft">
        {state?.client_bound
          ? `Your answers go to ${partnerName} and your team. When you finish, you are added to the team.`
          : `Your answers go to ${partnerName}. No account is created.`}
      </p>
    </Shell>
  );
};

export default PersonIntake;
